/**
 * As ações do Desk Cloud que falam com o servidor (docs/spec/desk-cloud.md): a lixeira e o desfazer,
 * mover, o tipo, o favorito, renomear, as pastas, as mesas, baixar e o zip, e a fila de envios. Os
 * menus, os botões e o teclado chamam as mesmas; cada uma relê a página no fim.
 */
import { api } from '../api.js';
import { forgetFileUrls, sendToCloud } from '../files.js';
import { translate, useLanguage } from '../i18n/index.js';
import { uploadProblem } from '../plans/planModel.js';
import { serverUrl } from '../serverUrl.js';
import type { DeskFile, DeskFolder, FileKind } from '../shared.js';
import { describeError, useApp } from '../store.js';
import { askConfirm, askText } from '../ui/prompt.js';
import { toast } from '../ui/Toaster.js';
import { useCloud, type CloudUpload } from './cloudStore.js';
import { useListen } from './listen.js';

const say = (key: string, params?: Record<string, string | number>) => translate(useLanguage.getState().language, key, params);

const reload = () => void useCloud.getState().load();

/** Roda a chamada com a sessão; o erro vira aviso. Devolve se deu certo. */
async function attempt(call: (token: string) => Promise<unknown>): Promise<boolean> {
  const token = useApp.getState().token;
  if (!token) return false;
  try {
    await call(token);
    return true;
  } catch (error) {
    toast(describeError(error), 'error');
    return false;
  } finally {
    reload();
  }
}

const ids = (files: readonly DeskFile[]) => files.map((file) => file.id);

/* ------------------------------------------------------------ lixeira */

export async function trashFiles(files: DeskFile[]): Promise<void> {
  if (!files.length) return;
  const linked = files.filter((file) => file.rooms.length).length;
  if (!(await attempt((token) => api.bulkFiles(token, { action: 'trash', ids: ids(files) })))) return;
  useCloud.getState().clearSelection();
  const text = files.length === 1 ? say('cloud.toast.trashedOne', { name: files[0]!.name }) : say('cloud.toast.trashed', { n: files.length });
  toast(linked ? `${text} ${say('cloud.toast.trashedRooms')}` : text, 'info', { label: say('cloud.undo'), run: () => void restoreFiles(files, false) });
}

export async function restoreFiles(files: DeskFile[], announce = true): Promise<void> {
  if (!files.length) return;
  if (!(await attempt((token) => api.bulkFiles(token, { action: 'restore', ids: ids(files) })))) return;
  useCloud.getState().clearSelection();
  if (announce) toast(files.length === 1 ? say('cloud.toast.restoredOne', { name: files[0]!.name }) : say('cloud.toast.restored', { n: files.length }), 'success');
}

export async function purgeFiles(files: DeskFile[]): Promise<void> {
  if (!files.length) return;
  const sure = await askConfirm({
    title: say('cloud.purge.title'),
    message: files.length === 1 ? say('cloud.purge.one', { name: files[0]!.name }) : say('cloud.purge.many', { n: files.length }),
    action: say('cloud.menu.purge'),
    danger: true,
  });
  if (!sure) return;
  if (await attempt((token) => api.bulkFiles(token, { action: 'purge', ids: ids(files) }))) useCloud.getState().clearSelection();
}

export async function emptyTrash(): Promise<void> {
  const count = useCloud.getState().summary?.views.trash ?? 0;
  if (!count) return;
  const sure = await askConfirm({ title: say('cloud.menu.emptyTrash'), message: say('cloud.purge.all', { n: count }), action: say('cloud.menu.emptyTrash'), danger: true });
  if (!sure) return;
  if (await attempt((token) => api.bulkFiles(token, { action: 'emptyTrash' }))) useCloud.getState().clearSelection();
}

/* ------------------------------------------------------------ organizar */

/** Move para a pasta (null: a raiz); o aviso desfaz devolvendo cada arquivo à pasta de antes. */
export async function moveFiles(files: DeskFile[], folderId: string | null): Promise<void> {
  const moving = files.filter((file) => file.folderId !== folderId);
  if (!moving.length) return;
  if (!(await attempt((token) => api.bulkFiles(token, { action: 'move', ids: ids(moving), folderId })))) return;
  if (folderId) useCloud.getState().usedFolder(folderId);
  const name = folderId ? (useCloud.getState().summary?.folders.find((folder) => folder.id === folderId)?.name ?? '') : say('cloud.root');
  const before = new Map<string | null, string[]>();
  for (const file of moving) before.set(file.folderId, [...(before.get(file.folderId) ?? []), file.id]);
  toast(say('cloud.toast.moved', { n: moving.length, folder: name }), 'info', {
    label: say('cloud.undo'),
    run: () =>
      void attempt(async (token) => {
        for (const [previous, group] of before) await api.bulkFiles(token, { action: 'move', ids: group, folderId: previous });
      }),
  });
}

export async function setKind(files: DeskFile[], kind: FileKind): Promise<void> {
  const changing = files.filter((file) => file.kind !== kind);
  if (changing.length) await attempt((token) => api.bulkFiles(token, { action: 'kind', ids: ids(changing), kind }));
}

export async function favoriteFiles(files: DeskFile[], on: boolean): Promise<void> {
  if (files.length) await attempt((token) => api.bulkFiles(token, { action: on ? 'favorite' : 'unfavorite', ids: ids(files) }));
}

export async function renameFile(file: DeskFile, name?: string): Promise<void> {
  const next = name ?? (await askText({ title: say('cloud.menu.rename'), initial: file.name, maxLength: 200, action: say('cloud.menu.rename') }));
  if (!next || next.trim() === file.name) return;
  await attempt((token) => api.patchFile(token, file.id, { name: next.trim() }));
}

/** Liga e desliga das mesas (o "Quem pode abrir"). */
export async function setRooms(files: DeskFile[], add: string[], remove: string[]): Promise<boolean> {
  if (!files.length || (!add.length && !remove.length)) return true;
  return attempt((token) => api.setFileRooms(token, ids(files), add, remove));
}

/* ------------------------------------------------------------ pastas */

export async function newFolder(parentId: string | null): Promise<string | null> {
  const name = await askText({ title: say('cloud.newFolder'), label: say('cloud.folder.name'), maxLength: 80, action: say('cloud.folder.create') });
  if (!name?.trim()) return null;
  let created: string | null = null;
  await attempt(async (token) => {
    created = (await api.createFolder(token, name.trim(), parentId)).id;
  });
  if (created) useCloud.getState().usedFolder(created);
  return created;
}

export async function renameFolder(folder: DeskFolder): Promise<void> {
  const name = await askText({ title: say('cloud.menu.rename'), initial: folder.name, maxLength: 80, action: say('cloud.menu.rename') });
  if (!name?.trim() || name.trim() === folder.name) return;
  await attempt((token) => api.updateFolder(token, folder.id, { name: name.trim() }));
}

export async function moveFolder(folder: DeskFolder, parentId: string | null): Promise<void> {
  if (parentId === folder.parentId || parentId === folder.id) return;
  await attempt((token) => api.updateFolder(token, folder.id, { parentId }));
}

/** Apaga a pasta e as de dentro; os arquivos delas vão para a lixeira. */
export async function deleteFolder(folder: DeskFolder): Promise<void> {
  const sure = await askConfirm({ title: say('cloud.menu.deleteFolder'), message: say('cloud.folder.deleteConfirm', { name: folder.name }), action: say('cloud.menu.deleteFolder'), danger: true });
  if (!sure) return;
  let trashed = 0;
  const ok = await attempt(async (token) => {
    trashed = (await api.deleteFolder(token, folder.id)).trashed;
  });
  if (ok) toast(say('cloud.toast.folderDeleted', { name: folder.name, n: trashed }), 'success');
}

/* ------------------------------------------------------------ baixar */

/** O link do arquivo, para copiar (um link novo, que expira como os outros). */
export async function copyLink(file: DeskFile): Promise<void> {
  const link = new URL(serverUrl(file.url), window.location.origin).toString();
  try {
    await navigator.clipboard.writeText(link);
    toast(say('cloud.toast.linkCopied'), 'success');
  } catch {
    await askText({ title: say('cloud.menu.copyLink'), label: say('cloud.copyManually'), initial: link, maxLength: 4000, action: say('common.close') });
  }
}

function navigateTo(href: string): void {
  const a = document.createElement('a');
  a.href = href;
  a.rel = 'noreferrer';
  document.body.append(a);
  a.click();
  a.remove();
}

/** Um arquivo baixa direto (o bucket manda como anexo); vários, num zip. */
export async function downloadFiles(files: DeskFile[]): Promise<void> {
  if (files.length === 1) {
    navigateTo(`${serverUrl(files[0]!.url)}&dl=1`);
    return;
  }
  await downloadZip({ ids: ids(files) });
}

export async function downloadZip(body: { ids: string[] } | { folderId: string } | { roomId: string }): Promise<void> {
  const token = useApp.getState().token;
  if (!token) return;
  try {
    navigateTo(await api.zipFiles(token, body));
  } catch (error) {
    toast(describeError(error), 'error');
  }
}

/* ------------------------------------------------------------ envios */

const controllers = new Map<string, AbortController>();
let running = false;
let uploadSeq = 0;

// a conta mudou (saiu, ou entrou outra nesta aba): a fila e a página eram da anterior. Sem isto, o
// que esperava na fila subiria para a conta seguinte, com a sessão dela
useApp.subscribe((state, previous) => {
  if (state.user?.id === previous.user?.id) return;
  for (const controller of controllers.values()) controller.abort();
  useCloud.getState().reset();
  useListen.getState().stop();
  forgetFileUrls();
});

/**
 * Põe arquivos na fila, para a mesa e a pasta dadas. A fila manda um de cada vez (o servidor também
 * recebe um por pessoa de cada vez), com o progresso de cada um.
 */
export function enqueueUploads(files: Iterable<File>, target: { roomId?: string | null; folderId?: string | null } = {}): void {
  const uploads: CloudUpload[] = [...files].map((file) => ({
    id: `up${++uploadSeq}`,
    file,
    roomId: target.roomId ?? null,
    folderId: target.folderId ?? null,
    status: 'waiting',
    progress: 0,
    error: null,
    planLimited: null,
    stored: null,
  }));
  if (!uploads.length) return;
  useCloud.getState().addUploads(uploads);
  void runQueue();
}

export function cancelUpload(id: string): void {
  const controller = controllers.get(id);
  if (controller) controller.abort();
  else useCloud.getState().patchUpload(id, { status: 'error', error: say('api.uploadCanceled') });
}

export function retryUpload(id: string): void {
  useCloud.getState().patchUpload(id, { status: 'waiting', progress: 0, error: null, planLimited: null });
  void runQueue();
}

async function runQueue(): Promise<void> {
  if (running) return;
  running = true;
  try {
    for (;;) {
      const next = useCloud.getState().uploads.find((upload) => upload.status === 'waiting');
      if (!next) break;
      await sendOne(next);
    }
  } finally {
    running = false;
  }
}

async function sendOne(upload: CloudUpload): Promise<void> {
  const cloud = useCloud.getState();
  const app = useApp.getState();
  // avisa antes de enviar o que o servidor recusaria (docs/spec/planos.md); o espaço soma os envios da fila
  const problem = uploadProblem(upload.file.size, app.plan, app.fileUsage?.used ?? null);
  if (problem) {
    cloud.patchUpload(upload.id, { status: 'error', planLimited: problem, error: null });
    return;
  }
  const controller = new AbortController();
  controllers.set(upload.id, controller);
  cloud.patchUpload(upload.id, { status: 'sending', progress: 0 });
  try {
    const stored = await sendToCloud(upload.file, {
      roomId: upload.roomId,
      folderId: upload.folderId,
      signal: controller.signal,
      onProgress: (progress) => useCloud.getState().patchUpload(upload.id, { progress }),
    });
    useCloud.getState().patchUpload(upload.id, { status: 'done', progress: 1, stored });
    const usage = useApp.getState().fileUsage;
    if (usage) useApp.setState({ fileUsage: { ...usage, used: usage.used + upload.file.size } });
    reload();
  } catch (error) {
    const code = (error as { code?: string }).code;
    const planLimited = code === 'too-large' ? 'too-large' : code === 'quota' ? 'no-space' : null;
    useCloud.getState().patchUpload(upload.id, { status: 'error', planLimited, error: planLimited ? null : describeError(error) });
  } finally {
    controllers.delete(upload.id);
  }
}
